// client/components/GraphExplainPanel.tsx
// Slide-up detail panel for node inspection

import React from 'react';

interface NodeMeta {
  uri?: string;
  file?: string;
  matchType?: string;
  _provider?: string;
  [key: string]: unknown;
}

interface GraphNodeData {
  id: string;
  name: string;
  kind: string;
  meta?: NodeMeta;
}

interface CheckItem {
  proof_ref?: string;
  target?: string;
  status?: string;
  message?: string;
  result?: string;
  category?: string;
  detail?: string;
  shapeId?: string;
  [key: string]: unknown;
}

interface StructuralData {
  nodes?: unknown[];
  edges?: unknown[];
  coverage?: number;
  maturity?: string;
  agreement?: number;
  [key: string]: unknown;
}

interface RunLogData {
  timestamp?: string;
  entries?: unknown[];
  job_id?: string;
  analyzed_at?: string;
  ontology_profile_hash?: string;
  shapes_version?: string;
  [key: string]: unknown;
}

type Props = {
  node: GraphNodeData | null;
  onClose: () => void;
  checks: CheckItem[];
  structural: StructuralData;
  runLog: RunLogData;
};

export function GraphExplainPanel({ node, onClose, checks, structural, runLog }: Props) {
  if (!node) return null;

  // Find related checks for this node
  const relatedChecks = checks.filter(c => {
    if (node.kind === 'term' && node.meta?.uri) {
      return c.proof_ref === node.meta.uri || c.target === node.name;
    }
    if (node.kind === 'column') {
      return c.target === node.name;
    }
    return false;
  });

  return (
    <div className="graph-explain-panel">
      <div className="panel-overlay" onClick={onClose} />

      <div className="panel-content">
        <header className="panel-header">
          <h2>{node.name}</h2>
          <button className="close-button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <div className="panel-body">
          <section className="node-info">
            <h3>Node Information</h3>
            <dl>
              <dt>ID:</dt>
              <dd><code>{node.id}</code></dd>

              <dt>Type:</dt>
              <dd><span className={`badge badge-${node.kind}`}>{node.kind}</span></dd>

              {node.kind === 'column' && node.meta?.file && (
                <>
                  <dt>File:</dt>
                  <dd>{node.meta.file}</dd>
                </>
              )}

              {node.kind === 'term' && (
                <>
                  {node.meta?.uri && (
                    <>
                      <dt>URI:</dt>
                      <dd>
                        <a
                          href={node.meta.uri}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="uri-link"
                        >
                          {node.meta.uri}
                        </a>
                      </dd>
                    </>
                  )}

                  {node.meta?.matchType && (
                    <>
                      <dt>Match Type:</dt>
                      <dd>
                        <span className={`badge badge-match-${node.meta.matchType.toLowerCase()}`}>
                          {node.meta.matchType}
                        </span>
                      </dd>
                    </>
                  )}

                  {node.meta?._provider && (
                    <>
                      <dt>Provider:</dt>
                      <dd>
                        <span className={`badge badge-provider-${node.meta._provider}`}>
                          {node.meta._provider}
                        </span>
                      </dd>
                    </>
                  )}
                </>
              )}
            </dl>
          </section>

          {relatedChecks.length > 0 && (
            <section className="related-checks">
              <h3>Related Checks ({relatedChecks.length})</h3>
              <div className="checks-list">
                {relatedChecks.map((check, i) => {
                  const resultClass = check.result?.toLowerCase() ?? 'unknown';
                  return (
                    <div key={i} className={`check check-${resultClass}`}>
                      <div className="check-header">
                        <span className={`result-badge result-${resultClass}`}>
                          {check.result ?? 'Unknown'}
                        </span>
                        <span className="check-category">{check.category ?? ''}</span>
                      </div>
                      <div className="check-detail">{check.detail ?? ''}</div>
                      {check.shapeId && (
                        <div className="check-shape">
                          <small>Shape: {check.shapeId}</small>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          <section className="metadata">
            <h3>Context</h3>
            <details>
              <summary>Run Log Info</summary>
              <dl>
                <dt>Job ID:</dt>
                <dd><code>{runLog.job_id || 'N/A'}</code></dd>

                <dt>Analyzed:</dt>
                <dd>{runLog.analyzed_at || 'N/A'}</dd>

                <dt>Ontology Profile Hash:</dt>
                <dd><code className="hash">{runLog.ontology_profile_hash || 'N/A'}</code></dd>

                {runLog.shapes_version && (
                  <>
                    <dt>SHACL Shapes Version:</dt>
                    <dd>{runLog.shapes_version}</dd>
                  </>
                )}
              </dl>
            </details>

            {structural && Object.keys(structural).length > 0 && (
              <details>
                <summary>Structural Quality</summary>
                <dl>
                  {structural.coverage !== undefined && (
                    <>
                      <dt>Coverage:</dt>
                      <dd>{(structural.coverage * 100).toFixed(0)}%</dd>
                    </>
                  )}

                  {structural.maturity && (
                    <>
                      <dt>Maturity:</dt>
                      <dd>{structural.maturity}</dd>
                    </>
                  )}

                  {structural.agreement !== undefined && (
                    <>
                      <dt>Agreement:</dt>
                      <dd>{(structural.agreement * 100).toFixed(0)}%</dd>
                    </>
                  )}
                </dl>
              </details>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
